import type { SyncConfig, SyncConfigRes } from '@tanstack/db'

/** Keep collection definitions synchronous while their shared storage opens asynchronously. */
export function deferredSync<T extends object, TKey extends string | number>(
  source: SyncConfig<T, TKey>,
  resolve: () => Promise<SyncConfig<T, TKey>>,
): SyncConfig<T, TKey> {
  return {
    ...source,
    sync: (params) => {
      let stopped = false
      let result: SyncConfigRes = {}
      const ready = resolve().then((config) => {
        if (stopped) return
        const started = config.sync(params)
        result = typeof started === 'function' ? { cleanup: started } : (started ?? {})
      })
      void ready.catch((error: unknown) => {
        if (!stopped) params.markError(error)
      })

      return {
        cleanup: () => {
          stopped = true
          return result.cleanup?.()
        },
        loadSubset: async (options) => {
          await ready
          if (!stopped) await result.loadSubset?.(options)
        },
        unloadSubset: (options) => {
          // Preserve load/unload ordering even when both arrive before storage is open.
          void ready
            .then(() => {
              if (!stopped) result.unloadSubset?.(options)
            })
            .catch(() => undefined)
        },
      }
    },
  }
}
